import React from 'react';
import Badge from 'shared/components/nv-badge';
import { useSelector } from 'react-redux';
import { Institution } from 'redux/schemas/models/institution';
import { getCurrentInstitution } from 'redux/selectors/institutions';

// Styles
import { css } from '@emotion/react';
import { black, gray2, gray6, info, white } from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { ModalType, NvModalProps } from './nv-modal';

type SkillViewModalProps = {
  header: string,
  skillTags: any[],
};

type SkillAndMetadataViewModalProps = SkillViewModalProps & {
  metadataTags: any[],
};

type SkillAndMetadataViewModal = ({ header, skillTags, metadataTags }: SkillAndMetadataViewModalProps) => NvModalProps;

type SkillViewModal = ({ header, skillTags }: SkillViewModalProps) => NvModalProps;

export const getModalSettingsForSkillView: SkillViewModal = ({ header, skillTags }) => ({
  header,
  type: ModalType.DYNAMIC,
  body: <NvSkillsView skillTags={skillTags} theme={NvSkillViewTheme.DARK} />,
  width: 600,
  fullHeight: false,
});

export const getModalSettingsForSkillAndMetadataView: SkillAndMetadataViewModal = ({ header, skillTags, metadataTags }) => ({
  header,
  type: ModalType.DYNAMIC,
  body: <NvSkillsAndMetadataView skillTags={skillTags} metadataTags={metadataTags} />,
  width: 600,
  fullHeight: false,
});

export enum NvSkillViewTheme {
  LIGHT = 'light',
  DARK = 'dark',
  INFO = 'info',
}

const themes = {
  [NvSkillViewTheme.LIGHT]: {
    backgroundColor: white,
    borderColor: gray2,
    color: black,
  },
  [NvSkillViewTheme.DARK]: {
    backgroundColor: gray6,
    borderColor: gray6,
    color: black,
  },
  [NvSkillViewTheme.INFO]: {
    backgroundColor: info,
    borderColor: info,
    color: black,
  },

};

type NvSkillViewProps = {
  skillTags: { id: number, name: string }[],
  theme?: NvSkillViewTheme,
};

const NvSkillsView = (props: NvSkillViewProps) => {
  const { skillTags, theme = NvSkillViewTheme.LIGHT } = props;
  const { backgroundColor, borderColor, color } = themes[theme];

  const styles = css`
    gap: ${halfSpacing}px;
    flex-wrap: wrap;
    span {
      overflow: hidden;
      white-space: nowrap;
    }
  `;

  return (
    <div className='d-flex justify-content-center' css={styles}>
      {skillTags?.map(({ id, name }) => (
        <Badge
          key={id}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          color={color}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

export const NvSkillsAndMetadataView = (props: NvSkillViewProps & { metadataTags: any[] }) => {
  const currentInstitution = useSelector<Institution>(getCurrentInstitution);
  const { skillTags, metadataTags, theme = NvSkillViewTheme.INFO } = props;
  const { backgroundColor, color } = themes[theme];

  const metadataList = metadataTags?.map(({ metadataFieldId, metadataValueId, metadataFieldName, metadataValueName }) => ({
    field: metadataFieldName ?? currentInstitution?.metadataFields?.find(field => field.id === metadataFieldId)?.name,
    value: metadataValueName ?? currentInstitution?.metadataFields?.find(field => field.id === metadataFieldId)?.metadataValues?.find(value => value.id === metadataValueId)?.value,
  }));

  const styles = css`
    gap: ${halfSpacing}px;
    flex-wrap: wrap;
    span {
      overflow: hidden;
      white-space: nowrap;
    }
  `;

  return (
    <div className='d-flex justify-content-center' css={styles}>
      {skillTags?.map(({ id, name }) => (
        <Badge
          key={id}
          backgroundColor={backgroundColor}
          color={color}
          iconName='skill'
          className='font-weight-bolder'
        >
          {name}
        </Badge>
      ))}
      {metadataList?.map(({ field, value }) => (
        <Badge
          key={field}
          backgroundColor={backgroundColor}
          color={color}
          iconName='skill-tag'
          className='font-weight-bolder'
        >
          {`${field}: ${value}`}
        </Badge>
      ))}
    </div>
  );
};

export default NvSkillsView;
